import { useState, useMemo, useCallback, type FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Search, Printer, UserPlus, Upload, ArrowUpDown, Eye, Pencil, FileDown, Sheet, ArrowLeft, Trash2,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventUser {
  id: number
  token: string
  createdAt: string | null
  firstName: string | null
  lastName: string | null
  phoneNumber: string | null
  email: string | null
  organization: string | null
  position: string | null
  gender: string | null
  country: string | null
  city: string | null
  age: number | null
  title: string | null
  idNumber: string | null
  passportNumber: string | null
  bloodType: string | null
  userType: string | null
  approved: boolean | null
  printed: boolean | null
  imageUrl: string | null
  customFields: Record<string, string>
}

const FIELD_KEY: Record<string, keyof EventUser> = {
  'Title': 'title',
  'First name': 'firstName',
  'Last name': 'lastName',
  'Phone number': 'phoneNumber',
  'Email': 'email',
  'Organization': 'organization',
  'Position': 'position',
  'Gender': 'gender',
  'Country': 'country',
  'City': 'city',
  'Age': 'age',
  'Id number': 'idNumber',
  'Passport number': 'passportNumber',
  'passport_number': 'passportNumber',
  'Blood type': 'bloodType',
  'Image': 'imageUrl',
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  registrationFields: string[]
  customFieldNames: string[]
  eventUsers: EventUser[]
  filter: string | null
  urls: AppShellProps['urls'] & {
    details: string
    editAdmin: string
    renderAll: string
    badgeBase: string
    search: string
    newAdmin: string
    import: string
    eventDashboard: string
    deleteBase: string
  }
  canDestroy: boolean
  translations: AppShellProps['translations'] & {
    registeredUsers: string
    for_: string
    registerDate: string
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
    organization: string
    position: string
    gender: string
    country: string
    city: string
    age: string
    title: string
    idNumber: string
    passportNumber: string
    bloodType: string
    image: string
    userType: string
    approved: string
    view: string
    true_: string
    false_: string
    pending: string
    search: string
    back: string
    newEventUser: string
    importUsers: string
    printSelected: string
    printed: string
    token: string
    badgeLink: string
    edit: string
    exportCsv: string
    exportExcel: string
    delete?: string
  }
}

const fieldLabel = (field: string, t: Props['translations']): string => {
  const map: Record<string, string> = {
    'Title': t.title, 'First name': t.firstName, 'Last name': t.lastName,
    'Phone number': t.phoneNumber, 'Email': t.email, 'Organization': t.organization,
    'Position': t.position, 'Gender': t.gender, 'Country': t.country,
    'City': t.city, 'Age': t.age, 'Id number': t.idNumber,
    'Passport number': t.passportNumber, 'passport_number': t.passportNumber,
    'Blood type': t.bloodType, 'Image': t.image,
  }
  return map[field] || field
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Report(props: Props) {
  const { event, registrationFields, customFieldNames, eventUsers, urls, canDestroy, translations: t, ...shell } = props

  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [sortCol, setSortCol] = useState<string>('id')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({})

  /* Unique values for column filters (computed from all rows) */
  const uniqueColValues = useMemo(() => {
    const SKIP = new Set(['First name', 'Last name', 'Email', 'Phone number', 'Id number', 'Passport number', 'Image', 'Token'])
    const result: Record<string, string[]> = {}
    // User type
    const uts = [...new Set(eventUsers.map(eu => eu.userType).filter(Boolean) as string[])].sort()
    if (uts.length > 1) result['userType'] = uts
    // Registration fields
    registrationFields.forEach(f => {
      if (SKIP.has(f)) return
      const k = FIELD_KEY[f]
      if (!k) return
      const vals = [...new Set(eventUsers.map(eu => String(eu[k] ?? '')).filter(Boolean))].sort()
      if (vals.length > 1 && vals.length <= 50) result[f] = vals
    })
    // Custom fields
    customFieldNames.forEach(name => {
      const vals = [...new Set(eventUsers.map(eu => eu.customFields[name] ?? '').filter(Boolean))].sort()
      if (vals.length > 1 && vals.length <= 50) result[name] = vals
    })
    return result
  }, [eventUsers, registrationFields, customFieldNames])

  const getColLabel = (col: string): string => {
    if (col === 'userType') return t.userType
    return fieldLabel(col, t) || col
  }

  /* Filter + sort */
  const filtered = useMemo(() => {
    let rows = eventUsers
    if (search.trim()) {
      const q = search.toLowerCase()
      rows = rows.filter(eu =>
        [eu.firstName, eu.lastName, eu.phoneNumber, eu.email, eu.organization, eu.token, eu.idNumber]
          .some(v => v && v.toLowerCase().includes(q)),
      )
    }
    // Column filters
    Object.entries(columnFilters).forEach(([col, val]) => {
      if (!val) return
      rows = rows.filter(eu => {
        if (col === 'userType') return eu.userType === val
        const k = FIELD_KEY[col]
        if (k) return String(eu[k] ?? '') === val
        return (eu.customFields[col] ?? '') === val
      })
    })
    rows = [...rows].sort((a, b) => {
      const aRaw = (a as unknown as Record<string, unknown>)[sortCol]
      const bRaw = (b as unknown as Record<string, unknown>)[sortCol]
      const av = typeof aRaw === 'number' ? aRaw : Number(aRaw) || 0
      const bv = typeof bRaw === 'number' ? bRaw : Number(bRaw) || 0
      if (!isNaN(av) && !isNaN(bv)) {
        return sortDir === 'asc' ? av - bv : bv - av
      }
      const as_ = String(aRaw ?? '')
      const bs_ = String(bRaw ?? '')
      return sortDir === 'asc' ? as_.localeCompare(bs_) : bs_.localeCompare(as_)
    })
    return rows
  }, [eventUsers, search, columnFilters, sortCol, sortDir])

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set())
    else setSelected(new Set(filtered.map(eu => eu.id)))
  }

  const showApproved = (val: boolean | null) => {
    if (val === true) return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{t.true_}</Badge>
    if (val === false) return <Badge variant="destructive">{t.false_}</Badge>
    return <Badge variant="outline" className="text-[#91918E]">{t.pending}</Badge>
  }

  const handlePrintSelected = (e: FormEvent) => {
    e.preventDefault()
    if (selected.size === 0) return
    const ids = Array.from(selected)
    const params = new URLSearchParams()
    ids.forEach(id => params.append('event_users[ids][]', id.toString()))
    params.append('format', 'pdf')
    window.open(`${urls.renderAll}?${params.toString()}`, '_blank')
  }

  /* Column count for empty state */
  const colCount = 5 + registrationFields.length + customFieldNames.length + 4

  /* ---- Export helpers --------------------------------------------- */
  const buildHeaderRow = useCallback((): string[] => {
    const headers = ['ID', t.registerDate]
    registrationFields.forEach(f => headers.push(fieldLabel(f, t)))
    customFieldNames.forEach(n => headers.push(n))
    headers.push(t.userType, t.token, t.printed)
    return headers
  }, [registrationFields, customFieldNames, t])

  const buildDataRows = useCallback((rows: EventUser[]): string[][] => {
    return rows.map(eu => {
      const cells: string[] = [String(eu.id), eu.createdAt ?? '']
      registrationFields.forEach(f => {
        const k = FIELD_KEY[f]
        if (f === 'Image') { cells.push(eu.imageUrl ?? ''); return }
        cells.push(k ? String(eu[k] ?? '') : '')
      })
      customFieldNames.forEach(name => {
        cells.push(eu.customFields[name] ?? eu.customFields[name.replace(/\?$/, '')] ?? '')
      })
      cells.push(eu.userType ?? '', eu.token ?? '', eu.printed ? t.true_ : t.false_)
      return cells
    })
  }, [registrationFields, customFieldNames, t])

  const escapeCsvCell = (cell: string): string => {
    if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
      return '"' + cell.replace(/"/g, '""') + '"'
    }
    return cell
  }

  const handleExportCsv = () => {
    const headers = buildHeaderRow()
    const rows = buildDataRows(filtered)
    const bom = '\uFEFF'
    const csv = bom + [headers, ...rows].map(r => r.map(escapeCsvCell).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    downloadBlob(blob, `${event.name}-users.csv`)
  }

  const handleExportExcel = () => {
    const headers = buildHeaderRow()
    const rows = buildDataRows(filtered)
    const escXml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n'
    xml += '<?mso-application progid="Excel.Sheet"?>\n'
    xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n'
    xml += '<Worksheet ss:Name="Users"><Table>\n'
    xml += '<Row>' + headers.map(h => `<Cell><Data ss:Type="String">${escXml(h)}</Data></Cell>`).join('') + '</Row>\n'
    rows.forEach(row => {
      xml += '<Row>' + row.map(c => `<Cell><Data ss:Type="String">${escXml(c)}</Data></Cell>`).join('') + '</Row>\n'
    })
    xml += '</Table></Worksheet></Workbook>'
    const blob = new Blob([xml], { type: 'application/vnd.ms-excel' })
    downloadBlob(blob, `${event.name}-users.xls`)
  }

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.registeredUsers} – ${event.name}`} />

      <div className="mx-auto max-w-[1400px] px-4 py-6">
        {/* Header */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-[#91918E] hover:text-[#37352F]" onClick={() => router.visit(urls.eventDashboard)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-semibold text-[#37352F]">
              {t.registeredUsers} {t.for_} {event.name}
            </h1>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="relative w-full sm:w-56">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#91918E]" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t.search}
                className="pl-9 border-[#E8E8E5]"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => router.visit(urls.newAdmin)}>
              <UserPlus className="h-4 w-4 mr-1" /> {t.newEventUser}
            </Button>
            <Button variant="outline" size="sm" onClick={() => router.visit(urls.import)}>
              <Upload className="h-4 w-4 mr-1" /> {t.importUsers}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCsv}>
              <FileDown className="h-4 w-4 mr-1" /> {t.exportCsv}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <Sheet className="h-4 w-4 mr-1" /> {t.exportExcel}
            </Button>
          </div>
        </div>

        {/* Print selected bar */}
        {selected.size > 0 && (
          <div className="mb-3 flex items-center gap-3 rounded-md border border-[#2383E2] bg-blue-50 px-4 py-2">
            <span className="text-sm font-medium text-[#2383E2]">{selected.size} selected</span>
            <Button size="sm" className="bg-[#2383E2] text-white" onClick={handlePrintSelected}>
              <Printer className="h-4 w-4 mr-1" /> {t.printSelected}
            </Button>
          </div>
        )}

        {/* Column filters */}
        {Object.keys(uniqueColValues).length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-[#E8E8E5] bg-[#F7F7F5] px-3 py-2">
            <span className="text-[11px] font-medium text-[#91918E] shrink-0">Filter:</span>
            {(Object.entries(uniqueColValues) as [string, string[]][]).map(([col, vals]) => (
              <div key={col} className="flex items-center gap-1">
                <span className="text-[11px] text-[#91918E] whitespace-nowrap">{getColLabel(col)}:</span>
                <select
                  value={columnFilters[col] || ''}
                  onChange={e => {
                    const v = e.target.value
                    setColumnFilters(prev => { const n = { ...prev }; if (v) n[col] = v; else delete n[col]; return n })
                  }}
                  className="h-6 rounded border border-[#E8E8E5] bg-white px-1 text-[11px] text-[#37352F] focus:outline-none"
                >
                  <option value="">All</option>
                  {vals.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            ))}
            {Object.keys(columnFilters).length > 0 && (
              <button onClick={() => setColumnFilters({})} className="text-[11px] text-[#EB5757] hover:underline ml-1">
                ✕ Clear
              </button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="rounded-lg border border-[#E8E8E5] bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10">
                  <Checkbox
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onCheckedChange={toggleAll}
                  />
                </TableHead>
                <TableHead className="w-14 cursor-pointer" onClick={() => toggleSort('id')}>
                  ID <ArrowUpDown className="inline h-3 w-3 ml-1" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => toggleSort('createdAt')}>
                  {t.registerDate} <ArrowUpDown className="inline h-3 w-3 ml-1" />
                </TableHead>
                {registrationFields.map(f => (
                  <TableHead key={f} className="cursor-pointer" onClick={() => toggleSort(FIELD_KEY[f] as string || f)}>
                    {fieldLabel(f, t)} <ArrowUpDown className="inline h-3 w-3 ml-1" />
                  </TableHead>
                ))}
                {customFieldNames.map(name => (
                  <TableHead key={`cf-${name}`}>{name}</TableHead>
                ))}
                <TableHead>{t.userType}</TableHead>
                <TableHead>{t.token}</TableHead>
                <TableHead>{t.printed}</TableHead>
                <TableHead>{t.badgeLink}</TableHead>
                <TableHead>{t.edit}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="py-8 text-center text-[#91918E]">
                    No results
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(eu => (
                  <TableRow key={eu.id} className="hover:bg-[#F1F1EF]">
                    <TableCell>
                      <Checkbox
                        checked={selected.has(eu.id)}
                        onCheckedChange={() => toggleSelect(eu.id)}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{eu.id}</TableCell>
                    <TableCell className="text-sm">{eu.createdAt}</TableCell>
                    {registrationFields.map(f => {
                      const k = FIELD_KEY[f]
                      if (f === 'Image') {
                        return (
                          <TableCell key={f}>
                            {eu.imageUrl && <img src={eu.imageUrl} alt="" className="h-8 w-8 rounded object-cover" />}
                          </TableCell>
                        )
                      }
                      return <TableCell key={f} className="text-sm">{k ? String(eu[k] ?? '') : ''}</TableCell>
                    })}
                    {customFieldNames.map(name => (
                      <TableCell key={`cf-${name}`} className="text-sm">
                        {eu.customFields[name] ?? eu.customFields[name.replace(/\?$/, '')] ?? ''}
                      </TableCell>
                    ))}
                    <TableCell className="text-sm">{eu.userType}</TableCell>
                    <TableCell className="font-mono text-xs">{eu.token}</TableCell>
                    <TableCell>{showApproved(eu.printed)}</TableCell>
                    <TableCell>
                      <a
                        href={`${urls.badgeBase}?id=${eu.token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#2383E2] hover:underline"
                      >
                        <Eye className="inline h-3.5 w-3.5 mr-1" />
                        {t.badgeLink}
                      </a>
                    </TableCell>
                    <TableCell>
                      <a
                        href={`${urls.editAdmin}?id=${eu.token}`}
                        className="text-sm text-[#2383E2] hover:underline"
                      >
                        <Pencil className="inline h-3.5 w-3.5 mr-1" />
                        {t.edit}
                      </a>
                    </TableCell>
                    {canDestroy && (
                      <TableCell>
                        <button
                          onClick={() => {
                            if (!window.confirm(`Delete user ID ${eu.id}? This cannot be undone.`)) return
                            router.delete(urls.deleteBase.replace(':id', eu.token), {
                              preserveScroll: true,
                              onSuccess: () => router.reload({ only: [] }),
                            })
                          }}
                          className="text-sm text-[#EB5757] hover:underline"
                        >
                          <Trash2 className="inline h-3.5 w-3.5 mr-1" />
                          {t.delete || 'Delete'}
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <p className="mt-3 text-sm text-[#91918E]">
          {filtered.length} / {eventUsers.length}
        </p>
      </div>
    </AppShell>
  )
}
